// Chat Store - Zustand state management for chat functionality

import { create } from 'zustand';
import {
  chatApi,
  ConversationWithDetails,
  ChatMessage,
  ChatParticipant,
  ChatReaction,
} from '../services/api';
import { notificationService } from '../services/notifications';
import { audioService } from '../services/audio.service';
import { useUIStore } from './ui.store';

interface ChatState {
  // Conversations
  conversations: ConversationWithDetails[];
  selectedConversationId: string | null;
  loadingConversations: boolean;
  conversationsError: string | null;

  // Messages
  messages: Map<string, ChatMessage[]>; // conversationId -> messages
  loadingMessages: Map<string, boolean>;
  messagesError: Map<string, string | null>;
  hasMoreMessages: Map<string, boolean>;

  // Typing indicators
  typingUsers: Map<string, string[]>; // conversationId -> userIds

  // UI state
  isComposing: boolean;
  replyingTo: ChatMessage | null;
  searchQuery: string;
  searchResults: ChatMessage[];

  // Unread counts
  totalUnreadCount: number;

  // New message flash indicator (for icon blink)
  newMessageFlash: boolean;
  clearNewMessageFlash: () => void;

  // Actions - Conversations
  fetchConversations: () => Promise<void>;
  selectConversation: (id: string | null) => void;
  createConversation: (
    type: 'direct' | 'group' | 'user_group',
    participantIds: string[],
    name?: string
  ) => Promise<ConversationWithDetails>;
  createDirectConversation: (userId: string) => Promise<ConversationWithDetails>;
  createFromUserGroup: (groupId: string) => Promise<ConversationWithDetails>;
  updateConversation: (
    id: string,
    data: { name?: string; description?: string; avatarUrl?: string | null }
  ) => Promise<void>;
  leaveConversation: (id: string) => Promise<void>;

  // Actions - Messages
  fetchMessages: (conversationId: string, loadMore?: boolean) => Promise<void>;
  sendMessage: (
    conversationId: string,
    content: string,
    options?: {
      messageType?: 'text' | 'image' | 'video' | 'audio' | 'file';
      replyToId?: string;
    }
  ) => Promise<ChatMessage>;
  updateMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  searchMessages: (conversationId: string, query: string) => Promise<void>;
  clearSearch: () => void;

  // Actions - Reactions
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;

  // Actions - Read receipts
  markConversationRead: (conversationId: string) => Promise<void>;

  // Actions - Typing indicators
  sendTyping: (conversationId: string) => Promise<void>;
  stopTyping: (conversationId: string) => Promise<void>;
  setTypingUsers: (conversationId: string, userIds: string[]) => void;

  // Actions - Pin/Mute
  pinConversation: (conversationId: string) => Promise<void>;
  unpinConversation: (conversationId: string) => Promise<void>;
  muteConversation: (conversationId: string, until?: Date) => Promise<void>;
  unmuteConversation: (conversationId: string) => Promise<void>;

  // Actions - UI
  setReplyingTo: (message: ChatMessage | null) => void;
  setIsComposing: (isComposing: boolean) => void;

  // Actions - WebSocket handlers
  handleNewMessage: (message: ChatMessage) => void;
  handleMessageUpdated: (message: ChatMessage) => void;
  handleMessageDeleted: (conversationId: string, messageId: string) => void;
  handleReactionAdded: (messageId: string, reaction: ChatReaction) => void;
  handleReactionRemoved: (
    messageId: string,
    userId: string,
    emoji: string
  ) => void;
  handleTypingIndicator: (conversationId: string, userId: string) => void;
  handleTypingStopped: (conversationId: string, userId: string) => void;
  handleParticipantAdded: (
    conversationId: string,
    participant: ChatParticipant
  ) => void;
  handleParticipantRemoved: (conversationId: string, userId: string) => void;
  handleConversationUpdated: (conversation: ConversationWithDetails) => void;
  handleReadReceipt: (conversationId: string, userId: string, messageId: string) => void;

  // Actions - Utility
  getConversationById: (id: string) => ConversationWithDetails | undefined;
  getOtherParticipant: (
    conversation: ConversationWithDetails,
    currentUserId: string
  ) => ChatParticipant | undefined;
  resetStore: () => void;
}

const initialState = {
  conversations: [] as ConversationWithDetails[],
  selectedConversationId: null as string | null,
  loadingConversations: false,
  conversationsError: null as string | null,
  messages: new Map<string, ChatMessage[]>(),
  loadingMessages: new Map<string, boolean>(),
  messagesError: new Map<string, string | null>(),
  hasMoreMessages: new Map<string, boolean>(),
  typingUsers: new Map<string, string[]>(),
  isComposing: false,
  replyingTo: null as ChatMessage | null,
  searchQuery: '',
  searchResults: [] as ChatMessage[],
  totalUnreadCount: 0,
  newMessageFlash: false,
};

let flashTimeout: ReturnType<typeof setTimeout> | null = null;

export const useChatStore = create<ChatState>((set, get) => ({
  ...initialState,

  clearNewMessageFlash: () => set({ newMessageFlash: false }),

  // ============================================
  // CONVERSATION ACTIONS
  // ============================================

  fetchConversations: async () => {
    set({ loadingConversations: true, conversationsError: null });
    try {
      const conversations = await chatApi.listConversations();
      const totalUnread = conversations.reduce(
        (sum, c) => sum + (c.unreadCount || 0),
        0
      );
      set({
        conversations,
        totalUnreadCount: totalUnread,
        loadingConversations: false,
      });
    } catch (error) {
      set({
        conversationsError: (error as Error).message,
        loadingConversations: false,
      });
    }
  },

  selectConversation: (id) => {
    set({ selectedConversationId: id, replyingTo: null });
    if (id) {
      get().fetchMessages(id);
      get().markConversationRead(id);
    }
  },

  createConversation: async (type, participantIds, name) => {
    const conversation = await chatApi.createConversation({
      type,
      participantIds,
      name,
    });
    set((state) => ({
      conversations: [conversation, ...state.conversations],
    }));
    return conversation;
  },

  createDirectConversation: async (userId) => {
    const conversation = await chatApi.createDirectConversation(userId);
    // Check if already exists
    const existing = get().conversations.find((c) => c.id === conversation.id);
    if (!existing) {
      set((state) => ({
        conversations: [conversation, ...state.conversations],
      }));
    }
    return conversation;
  },

  createFromUserGroup: async (groupId) => {
    const conversation = await chatApi.createFromUserGroup(groupId);
    const existing = get().conversations.find((c) => c.id === conversation.id);
    if (!existing) {
      set((state) => ({
        conversations: [conversation, ...state.conversations],
      }));
    }
    return conversation;
  },

  updateConversation: async (id, data) => {
    await chatApi.updateConversation(id, data);
    // Filter out null avatarUrl to match ConversationWithDetails type
    const updateData: Partial<ConversationWithDetails> = {
      ...(data.name !== undefined && { name: data.name }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.avatarUrl !== undefined && { avatarUrl: data.avatarUrl ?? undefined }),
    };
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, ...updateData } : c
      ),
    }));
  },

  leaveConversation: async (id) => {
    await chatApi.leaveConversation(id);
    set((state) => ({
      conversations: state.conversations.filter((c) => c.id !== id),
      selectedConversationId:
        state.selectedConversationId === id
          ? null
          : state.selectedConversationId,
    }));
  },

  // ============================================
  // MESSAGE ACTIONS
  // ============================================

  fetchMessages: async (conversationId, loadMore = false) => {
    const { messages, loadingMessages } = get();

    if (loadingMessages.get(conversationId)) return;

    const newLoadingMessages = new Map(loadingMessages);
    newLoadingMessages.set(conversationId, true);
    set({ loadingMessages: newLoadingMessages });

    try {
      const existingMessages = messages.get(conversationId) || [];
      const cursor = loadMore
        ? existingMessages[existingMessages.length - 1]?.id
        : undefined;

      const newMessages = await chatApi.getMessages(conversationId, {
        cursor,
        limit: 50,
      });

      const updatedMessages = new Map(get().messages);
      if (loadMore) {
        updatedMessages.set(conversationId, [
          ...existingMessages,
          ...newMessages,
        ]);
      } else {
        updatedMessages.set(conversationId, newMessages);
      }

      const newHasMore = new Map(get().hasMoreMessages);
      newHasMore.set(conversationId, newMessages.length === 50);

      const newLoadingMessagesAfter = new Map(get().loadingMessages);
      newLoadingMessagesAfter.set(conversationId, false);

      set({
        messages: updatedMessages,
        hasMoreMessages: newHasMore,
        loadingMessages: newLoadingMessagesAfter,
      });
    } catch (error) {
      const newMessagesError = new Map(get().messagesError);
      newMessagesError.set(conversationId, (error as Error).message);

      const newLoadingMessagesAfter = new Map(get().loadingMessages);
      newLoadingMessagesAfter.set(conversationId, false);

      set({
        messagesError: newMessagesError,
        loadingMessages: newLoadingMessagesAfter,
      });
    }
  },

  sendMessage: async (conversationId, content, options) => {
    const message = await chatApi.sendMessage(conversationId, {
      content,
      messageType: options?.messageType || 'text',
      replyToId: options?.replyToId,
    });

    // Add message to local state
    const updatedMessages = new Map(get().messages);
    const conversationMessages = updatedMessages.get(conversationId) || [];
    updatedMessages.set(conversationId, [message, ...conversationMessages]);

    // Update conversation's last message
    set((state) => ({
      messages: updatedMessages,
      replyingTo: null,
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              lastMessage: message,
              lastMessageAt: message.createdAt,
              lastMessageId: message.id,
            }
          : c
      ),
    }));

    return message;
  },

  updateMessage: async (messageId, content) => {
    const updatedMessage = await chatApi.updateMessage(messageId, content);

    // Update message in all relevant places
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        newMsgs[idx] = updatedMessage;
        updatedMessages.set(convId, newMsgs);
        break;
      }
    }

    set({ messages: updatedMessages });
  },

  deleteMessage: async (messageId) => {
    await chatApi.deleteMessage(messageId);

    // Mark message as deleted locally
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        newMsgs[idx] = { ...newMsgs[idx], isDeleted: true, content: undefined };
        updatedMessages.set(convId, newMsgs);
        break;
      }
    }

    set({ messages: updatedMessages });
  },

  searchMessages: async (conversationId, query) => {
    set({ searchQuery: query });
    if (!query.trim()) {
      set({ searchResults: [] });
      return;
    }

    const results = await chatApi.searchMessages(conversationId, query);
    set({ searchResults: results });
  },

  clearSearch: () => {
    set({ searchQuery: '', searchResults: [] });
  },

  // ============================================
  // REACTION ACTIONS
  // ============================================

  addReaction: async (messageId, emoji) => {
    const reaction = await chatApi.addReaction(messageId, emoji);

    // Update message locally
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        const message = { ...newMsgs[idx] };
        message.reactions = [...(message.reactions || []), reaction];
        newMsgs[idx] = message;
        updatedMessages.set(convId, newMsgs);
        break;
      }
    }

    set({ messages: updatedMessages });
  },

  removeReaction: async (messageId, emoji) => {
    await chatApi.removeReaction(messageId, emoji);

    // Remove reaction locally
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        const message = { ...newMsgs[idx] };
        message.reactions = (message.reactions || []).filter(
          (r) => r.emoji !== emoji
        );
        newMsgs[idx] = message;
        updatedMessages.set(convId, newMsgs);
        break;
      }
    }

    set({ messages: updatedMessages });
  },

  // ============================================
  // READ RECEIPT ACTIONS
  // ============================================

  markConversationRead: async (conversationId) => {
    try {
      await chatApi.markConversationRead(conversationId);

      // Update local unread count
      set((state) => {
        const conv = state.conversations.find((c) => c.id === conversationId);
        const unreadToRemove = conv?.unreadCount || 0;

        return {
          conversations: state.conversations.map((c) =>
            c.id === conversationId ? { ...c, unreadCount: 0 } : c
          ),
          totalUnreadCount: Math.max(0, state.totalUnreadCount - unreadToRemove),
        };
      });
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  },

  // ============================================
  // TYPING INDICATOR ACTIONS
  // ============================================

  sendTyping: async (conversationId) => {
    try {
      await chatApi.sendTyping(conversationId);
    } catch (error) {
      console.error('Failed to send typing indicator:', error);
    }
  },

  stopTyping: async (conversationId) => {
    try {
      await chatApi.stopTyping(conversationId);
    } catch (error) {
      console.error('Failed to stop typing indicator:', error);
    }
  },

  setTypingUsers: (conversationId, userIds) => {
    const newTypingUsers = new Map(get().typingUsers);
    if (userIds.length > 0) {
      newTypingUsers.set(conversationId, userIds);
    } else {
      newTypingUsers.delete(conversationId);
    }
    set({ typingUsers: newTypingUsers });
  },

  // ============================================
  // PIN/MUTE ACTIONS
  // ============================================

  pinConversation: async (conversationId) => {
    await chatApi.pinConversation(conversationId);

    // Update local state
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          const participant = c.participants?.find(p => !p.leftAt);
          if (participant) {
            return {
              ...c,
              participants: c.participants?.map(p =>
                p.id === participant.id ? { ...p, isPinned: true, pinnedAt: new Date().toISOString() } : p
              ),
            };
          }
        }
        return c;
      }),
    }));
  },

  unpinConversation: async (conversationId) => {
    await chatApi.unpinConversation(conversationId);

    // Update local state
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          const participant = c.participants?.find(p => !p.leftAt);
          if (participant) {
            return {
              ...c,
              participants: c.participants?.map(p =>
                p.id === participant.id ? { ...p, isPinned: false, pinnedAt: undefined } : p
              ),
            };
          }
        }
        return c;
      }),
    }));
  },

  muteConversation: async (conversationId, until) => {
    await chatApi.muteConversation(conversationId, until?.toISOString());

    // Update local state
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          const participant = c.participants?.find(p => !p.leftAt);
          if (participant) {
            return {
              ...c,
              participants: c.participants?.map(p =>
                p.id === participant.id ? { ...p, isMuted: true, mutedUntil: until?.toISOString() } : p
              ),
            };
          }
        }
        return c;
      }),
    }));
  },

  unmuteConversation: async (conversationId) => {
    await chatApi.unmuteConversation(conversationId);

    // Update local state
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          const participant = c.participants?.find(p => !p.leftAt);
          if (participant) {
            return {
              ...c,
              participants: c.participants?.map(p =>
                p.id === participant.id ? { ...p, isMuted: false, mutedUntil: undefined } : p
              ),
            };
          }
        }
        return c;
      }),
    }));
  },

  // ============================================
  // UI ACTIONS
  // ============================================

  setReplyingTo: (message) => {
    set({ replyingTo: message });
  },

  setIsComposing: (isComposing) => {
    set({ isComposing });
  },

  // ============================================
  // WEBSOCKET EVENT HANDLERS
  // ============================================

  handleNewMessage: (message) => {
    const state = get();
    const updatedMessages = new Map(state.messages);
    const conversationMessages = updatedMessages.get(message.conversationId) || [];

    // Check if message already exists (dedup)
    if (conversationMessages.some((m) => m.id === message.id)) {
      return;
    }

    updatedMessages.set(message.conversationId, [
      message,
      ...conversationMessages,
    ]);

    const isSelected = state.selectedConversationId === message.conversationId;

    // Show notification if message is from someone else and conversation is not selected
    if (message.sender && !isSelected) {
      const uiState = useUIStore.getState();
      const senderName = message.sender.firstName && message.sender.lastName
        ? `${message.sender.firstName} ${message.sender.lastName}`
        : message.sender.email || 'Someone';

      const messageContent = message.messageType === 'text'
        ? (message.content || 'Sent a message')
        : `Sent ${message.messageType === 'image' ? 'an image' : message.messageType === 'file' ? 'a file' : 'a message'}`;

      // Play notification sound if enabled (WAV file via audioService)
      if (uiState.chatSoundEnabled) {
        audioService.playSound(uiState.chatSoundType);
      }

      // Trigger icon flash for 5 seconds
      if (flashTimeout) clearTimeout(flashTimeout);
      set({ newMessageFlash: true });
      flashTimeout = setTimeout(() => {
        useChatStore.getState().clearNewMessageFlash();
      }, 5000);

      // Show notification if toast is enabled
      if (uiState.chatToastEnabled) {
        notificationService.notifyNewMessage(
          senderName,
          messageContent,
          message.conversationId,
          (convId) => {
            // Navigate to conversation when notification is clicked
            get().selectConversation(convId);
          }
        );
      }
    }

    // Update conversation's last message and increment unread if not selected
    set((prevState) => {
      return {
        messages: updatedMessages,
        conversations: prevState.conversations.map((c) => {
          if (c.id === message.conversationId) {
            return {
              ...c,
              lastMessage: message,
              lastMessageAt: message.createdAt,
              lastMessageId: message.id,
              unreadCount: isSelected ? c.unreadCount : (c.unreadCount || 0) + 1,
            };
          }
          return c;
        }),
        totalUnreadCount: isSelected
          ? prevState.totalUnreadCount
          : prevState.totalUnreadCount + 1,
      };
    });
  },

  handleMessageUpdated: (message) => {
    const updatedMessages = new Map(get().messages);
    const conversationMessages = updatedMessages.get(message.conversationId);

    if (conversationMessages) {
      const idx = conversationMessages.findIndex((m) => m.id === message.id);
      if (idx !== -1) {
        const newMsgs = [...conversationMessages];
        newMsgs[idx] = message;
        updatedMessages.set(message.conversationId, newMsgs);
        set({ messages: updatedMessages });
      }
    }
  },

  handleMessageDeleted: (conversationId, messageId) => {
    const updatedMessages = new Map(get().messages);
    const conversationMessages = updatedMessages.get(conversationId);

    if (conversationMessages) {
      const idx = conversationMessages.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...conversationMessages];
        newMsgs[idx] = { ...newMsgs[idx], isDeleted: true, content: undefined };
        updatedMessages.set(conversationId, newMsgs);
        set({ messages: updatedMessages });
      }
    }
  },

  handleReactionAdded: (messageId, reaction) => {
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        const message = { ...newMsgs[idx] };
        message.reactions = [...(message.reactions || []), reaction];
        newMsgs[idx] = message;
        updatedMessages.set(convId, newMsgs);
        set({ messages: updatedMessages });
        break;
      }
    }
  },

  handleReactionRemoved: (messageId, userId, emoji) => {
    const updatedMessages = new Map(get().messages);
    for (const [convId, msgs] of updatedMessages) {
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx !== -1) {
        const newMsgs = [...msgs];
        const message = { ...newMsgs[idx] };
        message.reactions = (message.reactions || []).filter(
          (r) => !(r.userId === userId && r.emoji === emoji)
        );
        newMsgs[idx] = message;
        updatedMessages.set(convId, newMsgs);
        set({ messages: updatedMessages });
        break;
      }
    }
  },

  handleTypingIndicator: (conversationId, userId) => {
    const newTypingUsers = new Map(get().typingUsers);
    const currentTypers = newTypingUsers.get(conversationId) || [];
    if (!currentTypers.includes(userId)) {
      newTypingUsers.set(conversationId, [...currentTypers, userId]);
      set({ typingUsers: newTypingUsers });
    }

    // Auto-remove after 10 seconds
    setTimeout(() => {
      get().handleTypingStopped(conversationId, userId);
    }, 10000);
  },

  handleTypingStopped: (conversationId, userId) => {
    const newTypingUsers = new Map(get().typingUsers);
    const currentTypers = newTypingUsers.get(conversationId) || [];
    const filtered = currentTypers.filter((id) => id !== userId);
    if (filtered.length > 0) {
      newTypingUsers.set(conversationId, filtered);
    } else {
      newTypingUsers.delete(conversationId);
    }
    set({ typingUsers: newTypingUsers });
  },

  handleParticipantAdded: (conversationId, participant) => {
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          return {
            ...c,
            participants: [...c.participants, participant],
          };
        }
        return c;
      }),
    }));
  },

  handleParticipantRemoved: (conversationId, userId) => {
    set((state) => ({
      conversations: state.conversations.map((c) => {
        if (c.id === conversationId) {
          return {
            ...c,
            participants: c.participants.filter((p) => p.userId !== userId),
          };
        }
        return c;
      }),
    }));
  },

  handleConversationUpdated: (conversation) => {
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversation.id ? { ...c, ...conversation } : c
      ),
    }));
  },

  handleReadReceipt: (conversationId, userId, messageId) => {
    set((state) => {
      const msgs = state.messages.get(conversationId);
      if (!msgs) return state;

      const newMsgs = msgs.map((m) => {
        // Mark the specified message and all earlier messages from same sender as read by this user
        if (m.id === messageId || (m.senderId !== userId && new Date(m.createdAt) <= new Date(msgs.find(mm => mm.id === messageId)?.createdAt || 0))) {
          const currentReadBy = m.readBy || [];
          if (currentReadBy.includes(userId)) return m;
          return { ...m, readBy: [...currentReadBy, userId] };
        }
        return m;
      });

      const newMap = new Map(state.messages);
      newMap.set(conversationId, newMsgs);
      return { messages: newMap };
    });
  },

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  getConversationById: (id) => {
    return get().conversations.find((c) => c.id === id);
  },

  getOtherParticipant: (conversation, currentUserId) => {
    if (conversation.type !== 'direct') return undefined;
    return conversation.participants.find((p) => p.userId !== currentUserId);
  },

  resetStore: () => {
    set(initialState);
  },
}));
